/**
 * Caching Strategy Rule
 * 
 * 1. Single Entry Point
 *    - All API calls MUST be in App.tsx's fetchData
 *    - Only two triggers: initial load and manual sync
 * 
 * 2. Cache-First Pattern
 *    if (!sync && cacheService.hasCache()) {
 *      // Use cached data
 *      return cacheService.getData();
 *    } else {
 *      // Fetch fresh data
 *      const data = await fetch('/api/...');
 *      cacheService.saveToCache(data);
 *    }
 * 
 * 3. Component Rules
 *    - NEVER make direct API calls
 *    - Either:
 *      a) Receive data via props
 *      b) Read from cacheService
 * 
 * 4. Cache Structure
 *    interface CacheData {
 *      data: T[];
 *      timestamp: number;
 *      version: string;
 *    }
 */

import { Room, Item, Etage, RoomItem } from '../types';

interface CacheData {
  rooms: Room[];
  items: Item[];
  etages: Etage[];
  roomItems: RoomItem[];
  timestamp: number;
  version: string;
  isSyncing: boolean;
  lastSyncError?: string;
  dataSource: 'cache' | 'api';
}

const CACHE_KEY = 'electro-form-cache';
const CACHE_VERSION = '1.0.0'; // Increment this when data structure changes

export const cacheService = {
  // Save data to cache
  saveToCache: (data: Partial<CacheData>, source: 'cache' | 'api' = 'api') => {
    try {
      const existingCache = cacheService.getFromCache();
      const newCache: CacheData = {
        ...existingCache,
        ...data,
        timestamp: Date.now(),
        version: CACHE_VERSION,
        isSyncing: false,
        dataSource: source,
        lastSyncError: undefined
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  },

  // Get data from cache
  getFromCache: (): CacheData => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (!cachedData) {
        return { 
          rooms: [], 
          items: [], 
          etages: [], 
          roomItems: [], 
          timestamp: 0, 
          version: CACHE_VERSION, 
          isSyncing: false,
          dataSource: 'cache'
        };
      }
      const parsedData = JSON.parse(cachedData);
      
      // Check if cache version matches current version
      if (parsedData.version !== CACHE_VERSION) {
        console.log('Cache version mismatch, clearing cache');
        cacheService.clearCache();
        return { 
          rooms: [], 
          items: [], 
          etages: [], 
          roomItems: [], 
          timestamp: 0, 
          version: CACHE_VERSION, 
          isSyncing: false,
          dataSource: 'cache'
        };
      }
      
      return parsedData;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return { 
        rooms: [], 
        items: [], 
        etages: [], 
        roomItems: [], 
        timestamp: 0, 
        version: CACHE_VERSION, 
        isSyncing: false,
        dataSource: 'cache'
      };
    }
  },

  // Check if cache exists
  hasCache: (): boolean => {
    const cache = cacheService.getFromCache();
    return cache.rooms.length > 0 || cache.items.length > 0 || cache.etages.length > 0 || cache.roomItems.length > 0;
  },

  // Set syncing state
  setSyncing: (isSyncing: boolean) => {
    try {
      const cache = cacheService.getFromCache();
      cache.isSyncing = isSyncing;
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error setting sync state:', error);
    }
  },

  // Set sync error
  setSyncError: (error: string) => {
    try {
      const cache = cacheService.getFromCache();
      cache.lastSyncError = error;
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (err) {
      console.error('Error setting sync error:', err);
    }
  },

  // Get last sync error
  getLastSyncError: (): string | undefined => {
    return cacheService.getFromCache().lastSyncError;
  },

  // Check if currently syncing
  isSyncing: (): boolean => {
    return cacheService.getFromCache().isSyncing;
  },

  // Get data source
  getDataSource: (): 'cache' | 'api' => {
    return cacheService.getFromCache().dataSource;
  },

  // Clear cache
  clearCache: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  },

  // Get specific data from cache
  getRooms: (): Room[] => {
    return cacheService.getFromCache().rooms;
  },

  getItems: (): Item[] => {
    return cacheService.getFromCache().items;
  },

  getEtages: (): Etage[] => {
    return cacheService.getFromCache().etages;
  },

  getRoomItems: (): RoomItem[] => {
    return cacheService.getFromCache().roomItems;
  },

  // Get cache timestamp
  getCacheTimestamp: (): number => {
    return cacheService.getFromCache().timestamp;
  }
}; 